import { AddAllergyDialog } from '@/components/molecules/AddAllergyDialog';
import { RecordListScroll } from '@liza/ui/components/RecordListScroll';
import { useCompleteRecordList } from '@liza/ui/hooks/useCompleteRecordList';
import { useTRPC, useTRPCClient } from '@/utils/trpc';
import type { PatientAdminApiRouterOutputs } from '@liza/patient-admin-api';
import { AllergiesList } from '@liza/ui/components/records/AllergiesList';
import {
  defaultFilters,
  RecordsListControls,
  type RecordsListFilters,
} from '@liza/ui/components/records/RecordsListControls';
import { useRecordPromotionStatus } from '@liza/ui/hooks/useRecordPromotionStatus';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDebouncedCallback } from 'use-debounce';
import type { DetailPanelView } from '../types';

type Patient = PatientAdminApiRouterOutputs['patient']['get']['patient'];

type Props = {
  onViewChange: (view: DetailPanelView | null) => void;
  patient: Patient;
  selectedId?: string;
};

export const Allergies = ({ onViewChange, patient, selectedId }: Props): React.ReactElement => {
  const { t } = useTranslation('records');
  const trpc = useTRPC();
  const trpcClient = useTRPCClient();
  const queryClient = useQueryClient();

  const [filters, setFilters] = useState<RecordsListFilters>(defaultFilters);
  const handleFiltersChange = useDebouncedCallback(setFilters, 300);
  const [isAddOpen, setIsAddOpen] = useState(false);

  const listOptions = {
    ownership: filters.ownership,
    searchParams: { 'code:text': filters.searchQuery },
    status: filters.status,
  };

  // The list groups records per allergen and orders by severity, so it needs the whole list first.
  const {
    items: allergies,
    query,
    isComplete,
  } = useCompleteRecordList({
    queryKey: trpc.patient.allergy.list.queryKey({ medplumPatientId: patient.medplumId, options: listOptions }),
    queryFn: async ({ offset, count }) => {
      const res = await trpcClient.patient.allergy.list.query({
        medplumPatientId: patient.medplumId,
        options: listOptions,
        offset,
        count,
      });
      return { items: res.allergies, hasMore: res.hasMore };
    },
  });

  const { mutateAsync: createAllergy } = useMutation(
    trpc.patient.allergy.create.mutationOptions({
      onSuccess: () => {
        void queryClient.invalidateQueries({
          queryKey: trpc.patient.allergy.list.queryKey({ medplumPatientId: patient.medplumId }),
        });
      },
    }),
  );

  const { promote, getPromotionStatus } = useRecordPromotionStatus();

  const handlePromotionClick = useCallback(
    (id: string): void => {
      const sourceAllergy = allergies.find((allergy) => allergy.id === id);
      if (!sourceAllergy) return;

      void promote(id, async () =>
        createAllergy({
          resource: {
            ...sourceAllergy,
            id: undefined,
            patient: { reference: `Patient/${patient.medplumId}` },
            recordedDate: new Date().toISOString(),
          },
        }),
      );
    },
    [createAllergy, allergies, patient, promote],
  );

  return (
    <div className="flex flex-col gap-2 p-2">
      <RecordsListControls
        title={t('allergies')}
        searchPlaceholder={t('searchAllergies')}
        onFiltersChange={handleFiltersChange}
        onAdd={() => setIsAddOpen(true)}
        addLabel={t('addAllergy')}
      />
      <AddAllergyDialog medplumPatientId={patient.medplumId} isOpen={isAddOpen} onOpenChange={setIsAddOpen} />
      <RecordListScroll isLoading={!query.isError && !isComplete} isError={query.isError} error={query.error}>
        <AllergiesList
          allergies={allergies}
          onSelectAllergy={(id) => onViewChange({ type: 'allergy-detail', id })}
          selectedAllergyId={selectedId}
          onPromotionClick={filters.ownership === 'external' ? handlePromotionClick : undefined}
          getPromotionStatus={getPromotionStatus}
        />
      </RecordListScroll>
    </div>
  );
};
